import type { PointItemNoteDoc } from '../types/pointItemNote';
import {
  loadPointItemNoteCache,
  loadPointItemNoteLoadedGroupIds,
  savePointItemNoteCache,
  savePointItemNoteLoadedGroupIds,
} from './pointItemNoteStorage';
import {
  isIsoNewer,
  isMainPresentationBrowserTab,
  postBroadcastChannel,
  subscribeBroadcastChannel,
  waitForCacheHit,
} from './noteDocTabBridgeCore';

const CHANNEL_NAME = 'cp-point-item-note-v1';
const REQUEST_CHANNEL_NAME = 'cp-point-item-note-request-v1';

export type PointItemNoteBridgeMessage = {
  v: 1;
  type: 'doc';
  workspaceId: string;
  gradeId: string;
  doc: PointItemNoteDoc;
  loadedGroupIds: string[];
  savedAt: number;
};

type PointItemNoteRequestMessage = {
  type: 'request';
  workspaceId: string;
  gradeId: string;
  /** 있으면 해당 그룹 이력까지 워밍 */
  groupId?: string;
};

let livePublished: {
  doc: PointItemNoteDoc;
  loadedGroupIds: string[];
} | null = null;

function isDocMessage(data: unknown): data is PointItemNoteBridgeMessage {
  if (!data || typeof data !== 'object') return false;
  const o = data as PointItemNoteBridgeMessage;
  return (
    o.v === 1 &&
    o.type === 'doc' &&
    typeof o.workspaceId === 'string' &&
    typeof o.gradeId === 'string' &&
    !!o.doc &&
    typeof o.doc === 'object' &&
    Array.isArray(o.loadedGroupIds)
  );
}

export function setLivePointItemNoteDoc(
  doc: PointItemNoteDoc | null,
  loadedGroupIds?: string[]
): void {
  if (!doc) {
    livePublished = null;
    return;
  }
  livePublished = {
    doc,
    loadedGroupIds:
      loadedGroupIds ??
      loadPointItemNoteLoadedGroupIds(doc.workspaceId, doc.gradeId),
  };
}

export function publishPointItemNoteDoc(
  doc: PointItemNoteDoc,
  loadedGroupIds?: string[]
): void {
  if (!doc.workspaceId || !doc.gradeId) return;
  const groups =
    loadedGroupIds ??
    loadPointItemNoteLoadedGroupIds(doc.workspaceId, doc.gradeId);
  livePublished = { doc, loadedGroupIds: groups };
  savePointItemNoteCache(doc, { force: true });
  savePointItemNoteLoadedGroupIds(doc.workspaceId, doc.gradeId, groups, {
    force: true,
  });
  const msg: PointItemNoteBridgeMessage = {
    v: 1,
    type: 'doc',
    workspaceId: doc.workspaceId,
    gradeId: doc.gradeId,
    doc,
    loadedGroupIds: groups,
    savedAt: Date.now(),
  };
  postBroadcastChannel(CHANNEL_NAME, msg);
}

export function requestPointItemNoteFromMainTab(input: {
  workspaceId: string;
  gradeId: string;
  groupId?: string;
}): void {
  postBroadcastChannel(REQUEST_CHANNEL_NAME, {
    type: 'request',
    workspaceId: input.workspaceId.trim(),
    gradeId: input.gradeId.trim(),
    groupId: input.groupId?.trim() || undefined,
  } satisfies PointItemNoteRequestMessage);
}

export function subscribePointItemNoteDoc(
  onDoc: (payload: { doc: PointItemNoteDoc; loadedGroupIds: string[] }) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const unsubBc = subscribeBroadcastChannel(
    CHANNEL_NAME,
    (msg) => {
      onDoc({ doc: msg.doc, loadedGroupIds: msg.loadedGroupIds });
    },
    isDocMessage
  );

  const onStorage = (e: StorageEvent) => {
    if (!e.key?.startsWith('cp.pointItemNote.v2.') || !e.newValue) return;
    try {
      const parsed = JSON.parse(e.newValue) as PointItemNoteDoc;
      if (!parsed?.workspaceId || !parsed?.gradeId) return;
      onDoc({
        doc: parsed,
        loadedGroupIds: loadPointItemNoteLoadedGroupIds(
          parsed.workspaceId,
          parsed.gradeId
        ),
      });
    } catch {
      /* ignore */
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    unsubBc();
    window.removeEventListener('storage', onStorage);
  };
}

export async function resolvePointItemNoteFromCacheOrMainTab(
  workspaceId: string,
  gradeId: string,
  groupId?: string | null
): Promise<PointItemNoteDoc | null> {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  const group = (groupId ?? '').trim();
  if (!ws || !gid) return null;

  const hitOk = (): boolean => {
    const cached = loadPointItemNoteCache(ws, gid);
    if (!cached) return false;
    if (!group) return true;
    if (loadPointItemNoteLoadedGroupIds(ws, gid).includes(group)) return true;
    /* presentationWarm은 loaded 메타 없이 그룹만 채울 수 있음 */
    return Object.prototype.hasOwnProperty.call(cached.memberPointsByGroup, group);
  };

  if (hitOk()) return loadPointItemNoteCache(ws, gid);

  await waitForCacheHit({
    tryHit: hitOk,
    onRequest: () =>
      requestPointItemNoteFromMainTab({
        workspaceId: ws,
        gradeId: gid,
        groupId: group || undefined,
      }),
  });

  return hitOk() ? loadPointItemNoteCache(ws, gid) : null;
}

export function installMainTabPointItemNoteResponder(): () => void {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return () => undefined;
  }

  let ch: BroadcastChannel | null = null;
  try {
    ch = new BroadcastChannel(REQUEST_CHANNEL_NAME);
    ch.onmessage = (ev: MessageEvent<PointItemNoteRequestMessage>) => {
      const msg = ev.data;
      if (!msg || msg.type !== 'request') return;
      const workspaceId = (msg.workspaceId ?? '').trim();
      const gradeId = (msg.gradeId ?? '').trim();
      const groupId = (msg.groupId ?? '').trim() || undefined;
      if (!workspaceId || !gradeId) return;
      void warmPointItemNoteForOpen({ workspaceId, gradeId, groupId });
    };
  } catch {
    ch = null;
  }

  return () => {
    try {
      ch?.close();
    } catch {
      /* ignore */
    }
  };
}

export async function warmPointItemNoteForOpen(input: {
  workspaceId: string;
  gradeId: string;
  groupId?: string;
}): Promise<{ ok: boolean; reason: string }> {
  const workspaceId = input.workspaceId.trim();
  const gradeId = input.gradeId.trim();
  const groupId = (input.groupId ?? '').trim();
  if (!workspaceId || !gradeId) return { ok: false, reason: 'workspace/grade 없음' };

  try {
    const {
      getPointItemNote,
      ensurePointItemNoteGroup,
    } = await import('../services/pointItemNoteService');

    let doc: PointItemNoteDoc;
    let loadedGroupIds: string[];

    if (
      livePublished &&
      livePublished.doc.workspaceId === workspaceId &&
      livePublished.doc.gradeId === gradeId
    ) {
      doc = livePublished.doc;
      loadedGroupIds = [...livePublished.loadedGroupIds];
    } else {
      const cached = loadPointItemNoteCache(workspaceId, gradeId);
      if (cached) {
        doc = cached;
        loadedGroupIds = loadPointItemNoteLoadedGroupIds(workspaceId, gradeId);
      } else if (isMainPresentationBrowserTab()) {
        return { ok: false, reason: 'presentation-no-db' };
      } else {
        doc = await getPointItemNote(workspaceId, gradeId);
        loadedGroupIds = loadPointItemNoteLoadedGroupIds(workspaceId, gradeId);
      }
    }

    const groupCached =
      !!groupId &&
      Object.prototype.hasOwnProperty.call(doc.memberPointsByGroup, groupId);
    if (groupId && !loadedGroupIds.includes(groupId) && !groupCached) {
      if (isMainPresentationBrowserTab()) {
        publishPointItemNoteDoc(doc, loadedGroupIds);
        return { ok: false, reason: 'presentation-no-db-group' };
      }
      doc = await ensurePointItemNoteGroup(
        workspaceId,
        gradeId,
        groupId,
        doc,
        undefined,
        { presentationWarm: true }
      );
      loadedGroupIds = loadPointItemNoteLoadedGroupIds(workspaceId, gradeId);
    }

    publishPointItemNoteDoc(doc, loadedGroupIds);
    return {
      ok: true,
      reason: groupId ? `group:${groupId}` : 'settings',
    };
  } catch (err) {
    console.warn('[pointItemNoteTabBridge] warm 실패', err);
    return { ok: false, reason: 'error' };
  }
}

export function shouldApplyRemotePointItemNote(options: {
  remote: PointItemNoteDoc;
  local: PointItemNoteDoc | null;
  isDirty: boolean;
}): boolean {
  if (options.isDirty) return false;
  const { remote, local } = options;
  if (!local) return true;
  if (local.workspaceId !== remote.workspaceId || local.gradeId !== remote.gradeId) {
    return false;
  }
  if (local.updatedAt === remote.updatedAt) return true;
  return isIsoNewer(remote.updatedAt, local.updatedAt);
}
